import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import AdminLayout from "@/components/admin/AdminLayout";
import axios from "axios";
import { Plus, Trash2, X, Save, Loader2, UserCog, Shield } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_COLORS = {
  admin:       "bg-purple-50 text-purple-700 border-purple-100",
  team_member: "bg-blue-50 text-blue-700 border-blue-100",
};

export default function AdminTeam() {
  const { authHeader, user: currentUser } = useAuth();
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "team_member" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const fetchTeam = () => {
    setLoading(true);
    axios.get(`${API}/team`, { headers: authHeader() })
      .then(r => setTeam(r.data.team || []))
      .finally(() => setLoading(false));
  };
  useEffect(fetchTeam, []);

  const handleSave = async () => {
    if (!form.name || !form.email || !form.password) {
      setError("Please fill in all fields."); return;
    }
    setSaving(true); setError("");
    try {
      await axios.post(`${API}/team`, form, { headers: authHeader() });
      setShowModal(false);
      setForm({ name: "", email: "", password: "", role: "team_member" });
      fetchTeam();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to create team member.");
    }
    setSaving(false);
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Remove ${name} from the team?`)) return;
    await axios.delete(`${API}/team/${id}`, { headers: authHeader() }).catch(() => {});
    fetchTeam();
  };

  return (
    <AdminLayout title="Team">
      <div className="space-y-4 max-w-2xl">
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">{team.length} team member{team.length !== 1 ? "s" : ""}</p>
          {currentUser?.role === "admin" && (
            <button onClick={() => setShowModal(true)}
              className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-cyan-500 text-white px-5 py-2.5 rounded-full text-sm font-medium hover:opacity-90 transition-all shadow-md shadow-blue-600/20">
              <Plus size={16} /> Add Team Member
            </button>
          )}
        </div>

        <div className="space-y-3">
          {loading ? (
            <div className="bg-white rounded-2xl border border-slate-100 p-10 text-center text-slate-400 text-sm">Loading...</div>
          ) : team.map(member => (
            <div key={member.id}
              className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_20px_rgb(0,0,0,0.04)] p-5 flex items-center gap-4">
              <div className="w-11 h-11 rounded-full bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center flex-shrink-0 shadow-md shadow-blue-500/20">
                <span className="text-white font-semibold text-sm">
                  {member.name?.charAt(0) || member.email?.charAt(0) || "?"}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-slate-900 text-sm">{member.name || "—"}</p>
                  {member.id === currentUser?.id && (
                    <span className="text-xs text-slate-400 italic">(you)</span>
                  )}
                </div>
                <p className="text-sm text-slate-400">{member.email}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-xs font-medium border ${ROLE_COLORS[member.role] || "bg-slate-100 text-slate-500 border-slate-200"}`}>
                  {member.role === "admin" ? "Admin" : "Team Member"}
                </span>
                {currentUser?.role === "admin" && member.id !== currentUser?.id && (
                  <button onClick={() => handleDelete(member.id, member.name || member.email)}
                    className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all">
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Access notice */}
        <div className="bg-blue-50 rounded-2xl border border-blue-100 p-4 flex gap-3">
          <Shield size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-blue-700 leading-relaxed">
            Team members can view leads, add notes, and update statuses. Only admins can add or remove team members, access webhook settings, or export data.
          </p>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm">
            <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <UserCog size={18} className="text-blue-600" />
                <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Add Team Member</h3>
              </div>
              <button onClick={() => setShowModal(false)} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-50">
                <X size={18} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1.5">Full Name *</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder="Sarah Johnson"
                  className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1.5">Email *</label>
                <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                  placeholder="sarah@apexumpartners.com"
                  className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1.5">Password *</label>
                <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
                  placeholder="Minimum 8 characters"
                  className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1.5">Role</label>
                <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}
                  className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none bg-white">
                  <option value="team_member">Team Member</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              {error && <p className="text-xs text-red-500 bg-red-50 border border-red-100 rounded-xl px-4 py-2.5">{error}</p>}
            </div>
            <div className="px-6 py-4 border-t border-slate-100 flex gap-3">
              <button onClick={() => setShowModal(false)} className="flex-1 py-2.5 text-sm border border-slate-200 rounded-full text-slate-600 hover:bg-slate-50 transition-colors">
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-cyan-500 text-white py-2.5 rounded-full text-sm font-medium hover:opacity-90 disabled:opacity-50 transition-all shadow-md shadow-blue-600/20">
                {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                {saving ? "Adding..." : "Add Member"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
